import React, { useCallback, useContext, useEffect } from 'react';
import {
  Form,
  Modal,
  Row,
  InputNumber,
  Col,
  Switch,
  DatePicker,
  Divider,
  Card,
  Tabs,
  notification,
  Button,
} from 'antd';
import { FormInstance, useForm } from 'antd/lib/form/Form';
import TextArea from 'antd/lib/input/TextArea';
import { CloseOutlined, CheckOutlined } from '@ant-design/icons';
import {
  dateToString,
  shouldUpdateCompare,
  toDatePickerDate,
} from 'utils/utils2';
import FormInput from 'components/FormInput';
import { toCurrency, toRate } from '../../../utils/utils';
import ThirdPartyInput from 'components/ThirdPartyInput';
import ParListSelect from 'components/ParListSelect';
import { AssetComponent } from 'services/assets';
import Value from 'components/Value';
import TaxSelect from 'components/TaxSelect';
import GetLabel from 'DisplayFramework/components/GetLabel';
import InputCurrency from 'components/InputCurrency';
import InputRate from 'components/InputRate';
import { onFinishFailed } from 'components/DisplayValidationErrors/DisplayValidationErrors';
import FormItem from 'antd/lib/form/FormItem';
import { isNil, sumBy } from 'lodash';
import TabAssetComponentSchedule from './AssetComponentSchedule';
import { useTranslation } from 'react-i18next';
import { AuthContext } from 'components/Auth/Auth';
import _ from 'lodash';

export type AssetComponentModalProps = {
  formC: FormInstance;
  addorupdate: 'ADD' | 'UPDATE';
  visible: boolean;
  iscreate?: boolean;
  onCancel: () => void;
  onOk: (item: AssetComponent) => void;
  currcode: string;
  compid?: number;
  record?: any;
  linkedCon?: boolean;
  status?: string;
  fromproposal?: boolean;
};
const { TabPane } = Tabs;
const initialValues = {
  compid: -1,
  suppliername: '',
  supplierreference: '',
  comptype: 'ASSET',
  compcategory: '',
  compdescription: '',
  comporiginalnetamount: 0,
  compdiscountrate: 0,
  compnetamount: 0,
  taxcode: '',
  taxrate: 0,
  comptaxamount: 0,
  compgrossamount: 0,
  asset: null as any,
  compdiscountamount: 0,
  compfree1: '',
  tpidsupplier: 0,
  flagholdback: false,
  holdbackpercentage: 0,
  holdbackamount: 0,
  holdbackbeleasenbdays: 0,
  holdbackbeleasedate: '',
};

const AssetComponentModal = (props: AssetComponentModalProps) => {
  const {
    visible,
    onCancel,
    onOk,
    addorupdate,
    formC,
    compid,
    currcode,
    record,
    linkedCon,
    status,
    fromproposal,
    iscreate,
  } = props;
  const [form] = useForm();
  const { t } = useTranslation();
  const { isInspect } = useContext(AuthContext);

  const recalculate = useCallback(
    (updatedValues: any) => {
      let comporiginalnetamount = form.getFieldValue('comporiginalnetamount');
      let compdiscountrate = form.getFieldValue('compdiscountrate');
      let taxrate =
        updatedValues?.taxrate !== undefined
          ? updatedValues?.taxrate
          : form.getFieldValue('taxrate');
      let compnetamount =
        comporiginalnetamount -
        comporiginalnetamount * (compdiscountrate / 100);
      let comptaxamount = compnetamount * (taxrate / 100);
      let compgrossamount = compnetamount + comptaxamount;
      form.setFieldsValue({
        taxrate: taxrate,
        comporiginalnetamount: comporiginalnetamount,
        compdiscountrate: compdiscountrate,
        compnetamount: compnetamount,
        comptaxamount: comptaxamount,
        compgrossamount: compgrossamount,
      });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [formC]
  );
  useEffect(() => {
    if (addorupdate === 'UPDATE') {
      if (record?.assetcompschedule?.length > 0)
        form.setFieldsValue({
          totalHoldBackAmount: sumBy(
            record?.assetcompschedule,
            'holdbackamount'
          ),
          assetscheduledamount: sumBy(record?.assetcompschedule, 'payamount'),
          compassetschedules: record?.assetcompschedule?.map((ele: any) => ({
            ...ele,
            holdbackprevreleasedate: toDatePickerDate(
              ele.holdbackprevreleasedate
            ),
            holdbackreleasedate: toDatePickerDate(ele.holdbackreleasedate),
            paydate: toDatePickerDate(ele.paydate),
            payduedate: toDatePickerDate(ele.payduedate),
            previsionalduedate: toDatePickerDate(ele.previsionalduedate),
            action: 'UPDATE',
          })),
        });
      form.setFieldsValue({
        compid: record?.compid,
        suppliername: record?.suppliername,
        supplierreference: record?.supplierreference,
        comptype: record?.comptype,
        compcategory: record?.compcategory,
        compdescription: record?.compdescription,
        comporiginalnetamount: record?.comporiginalnetamount,
        compdiscountrate: record?.compdiscountrate,
        compnetamount: record?.compnetamount,
        taxcode: record?.taxcode,
        taxrate: record?.taxrate,
        comptaxamount: record?.comptaxamount,
        compgrossamount: record?.compgrossamount,
        asset: record?.asset,
        compdiscountamount: record?.compdiscountamount,
        compfree1: record?.compfree1,
        tpidsupplier: record?.tpidsupplier,
        flagholdback: record?.flagholdback,
        holdbackpercentage: record?.holdbackpercentage,
        holdbackamount: record?.holdbackamount,
        holdbackbeleasenbdays: record?.holdbackbeleasenbdays,
        holdbackbeleasedate: toDatePickerDate(record?.holdbackbeleasedate),
        supplier: record.supplier ?? {
          tpid: record?.tpidsupplier,
          tpname: record?.suppliername,
          tpreference: record?.supplierreference,
        },
        requireSave: false,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visible]);
  const handleFinish = (values: any) => {
    onOk({
      ...values,
      compid:
        addorupdate === 'ADD' ? Math.round(_.random(10000000)) : compid,
      suppliername: values?.supplier?.tpname,
      supplier: values.supplier,
      supplierreference: values?.supplier?.tpreference,
      tpidsupplier: values?.supplier?.tpid,
      holdbackbeleasedate:
        dateToString(values?.holdbackbeleasedate ?? '') ?? '',
      taxrate: values.taxrate,
      holdbackpercentage: values.holdbackpercentage ?? 0,
      holdbackamount: values.holdbackamount ?? 0,
      holdbackbeleasenbdays: values.holdbackbeleasenbdays ?? 0,
      assetid: 0,
    });
    form.resetFields();
  };
  return (
    <Modal
      visible={visible}
      title={
        addorupdate === 'ADD' ? (
          <GetLabel Inspectstyle={{ width: '30%' }} label="AddAssetComponent" />
        ) : (
          <GetLabel
            Inspectstyle={{ width: '30%' }}
            label="updateAssetComponent"
          />
        )
      }
      okText="Add"
      onCancel={() => {
        if (
          form.getFieldValue('requireSave') === false ||
          isNil(form.getFieldValue('requireSave'))
        ) {
          onCancel();
          form.resetFields();
        } else notification.error({ message: t('AssetCompSchSave') });
      }}
      footer={[
        <Button key="cancel" onClick={() => !isInspect && onCancel()}>
          <GetLabel label="cancel" />
        </Button>,
        <Button
          key="add"
          type="primary"
          onClick={() => {
            if (!isInspect)
              if (
                form.getFieldValue('requireSave') === false ||
                isNil(form.getFieldValue('requireSave'))
              ) {
                form.submit();
              } else notification.error({ message: t('AssetCompSchSave') });
          }}
        >
          <GetLabel label="add" />
        </Button>,
      ]}
      width="1400px"
      style={{ top: 20 }}
    >
      {' '}
      <Form
        form={form}
        onFinish={handleFinish}
        onFinishFailed={onFinishFailed}
        onValuesChange={(field) => {
          if (field.compassetschedules) {
            form.setFieldsValue({ requireSave: true });
          }
        }}
        layout="vertical"
        initialValues={initialValues}
        labelCol={{ span: 24 }}
      >
        <Tabs animated defaultActiveKey="1" type="card">
          <TabPane tab={<GetLabel label="AssetDetail" />} key="1">
            <Card>
              <Row gutter={16}>
                <Col span={16}>
                  <Row>
                    <FormInput
                      rules={[{ required: true }]}
                      comcode="assettype"
                      name="comptype"
                      col
                      xs={12}
                      shouldUpdate
                    >
                      <ParListSelect
                        parcode="COMPTYPE"
                        disabled={
                          fromproposal
                            ? status !== 'PROPOSAL' &&
                              status !== undefined &&
                              status !== 'INIT'
                            : linkedCon ||
                              status === 'PROPOSAL' ||
                              status === 'ORDER' ||
                              status === 'DELIVER'
                        }
                      />
                    </FormInput>
                    <Form.Item
                      rules={[{ required: true }]}
                      shouldUpdate={shouldUpdateCompare('comptype')}
                      noStyle
                    >
                      {({ getFieldValue }) => (
                        <FormInput
                          comcode="assetcategory"
                          col
                          xs={12}
                          name="compcategory"
                          rules={[{ required: true }]}
                        >
                          <ParListSelect
                            parcode="COMPCAT"
                            parvalueparent={getFieldValue('comptype')}
                            disabled={
                              fromproposal
                                ? status !== 'PROPOSAL' &&
                                  status !== undefined &&
                                  status !== 'INIT'
                                : linkedCon ||
                                  status === 'PROPOSAL' ||
                                  status === 'ORDER' ||
                                  status === 'DELIVER'
                            }
                          />
                        </FormInput>
                      )}
                    </Form.Item>
                  </Row>
                  <Row>
                    {/*
                    The supplier should be mandatory, important in tax calculation, and its absence poses a problem. !!!!!
                    */}
                    <FormInput
                      col
                      xs={24}
                      comcode="supplier"
                      rules={[{ required: true }]}
                      name="supplier"
                    >
                      <ThirdPartyInput
                        role="SUPPLIER"
                        display={
                          fromproposal
                            ? status !== 'PROPOSAL' &&
                              status !== undefined &&
                              status !== 'INIT'
                            : linkedCon ||
                              status === 'PROPOSAL' ||
                              status === 'ORDER' ||
                              status === 'DELIVER'
                        }
                      />
                    </FormInput>

                    <FormInput
                      col
                      xs={24}
                      name="compdescription"
                      comcode="assetdescription"
                      rules={[{ required: true }]}
                    >
                      <TextArea
                        rows={7}
                        disabled={
                          fromproposal
                            ? status !== 'PROPOSAL' &&
                              status !== undefined &&
                              status !== 'INIT'
                            : linkedCon ||
                              status === 'PROPOSAL' ||
                              status === 'ORDER' ||
                              status === 'DELIVER'
                        }
                      />
                    </FormInput>
                  </Row>
                </Col>
                <Col span={8}>
                  <Card type="inner" style={{ background: '#F6F3F2' }}>
                    <Row gutter={16}>
                      <InputCurrency
                        col
                        md={12}
                        onChange={(value) =>
                          recalculate({ comporiginalnetamount: value })
                        }
                        currcode={currcode}
                        fieldname="comporiginalnetamount"
                        comcode="originalnetamount"
                        rules={[{ required: true }]}
                        disabled={
                          fromproposal
                            ? status !== 'PROPOSAL' &&
                              status !== undefined &&
                              status !== 'INIT'
                            : linkedCon ||
                              status === 'PROPOSAL' ||
                              status === 'ORDER' ||
                              status === 'DELIVER'
                        }
                      />
                      <FormInput
                        col
                        xs={12}
                        name="compdiscountrate"
                        comcode="discount"
                      >
                        <InputNumber
                          min={0}
                          max={100}
                          formatter={(value) => `${value}%`}
                          onChange={(value) =>
                            recalculate({ discountRate: value })
                          }
                          disabled={
                            fromproposal
                              ? status !== 'PROPOSAL' &&
                                status !== undefined &&
                                status !== 'INIT'
                              : linkedCon ||
                                status === 'PROPOSAL' ||
                                status === 'ORDER' ||
                                status === 'DELIVER'
                          }
                        />
                      </FormInput>

                      <FormInput
                        col
                        xs={12}
                        name="compnetamount"
                        comcode="netamount"
                      >
                        <Value
                          formatter={(value) => toCurrency(value, currcode)}
                        />
                      </FormInput>
                      <FormInput
                        name="taxcode"
                        col
                        xs={12}
                        shouldUpdate
                        comcode="taxcode"
                        rules={[{ required: true }]}
                      >
                        <TaxSelect
                          taxflow="INOUT"
                          disabled={
                            fromproposal
                              ? status !== 'PROPOSAL' &&
                                status !== undefined &&
                                status !== 'INIT'
                              : linkedCon ||
                                status === 'PROPOSAL' ||
                                status === 'ORDER' ||
                                status === 'DELIVER'
                          }
                          onSelected={(tax) =>
                            recalculate({ taxrate: tax.taxrate })
                          }
                        />
                      </FormInput>

                      <FormInput
                        col
                        xs={24}
                        md={12}
                        name="comptaxamount"
                        comcode="taxamount"
                      >
                        <Value
                          formatter={(value) => toCurrency(value, currcode)}
                        />
                      </FormInput>
                      <FormInput
                        col
                        xs={24}
                        md={12}
                        name="compgrossamount"
                        comcode="grossamount"
                      >
                        <Value
                          formatter={(value) => toCurrency(value, currcode)}
                        />
                      </FormInput>
                      <FormInput
                        col
                        xs={12}
                        name="taxrate"
                        comcode="taxrate"
                        hidden
                      >
                        <Value formatter={toRate} />
                      </FormInput>
                    </Row>
                  </Card>
                </Col>
              </Row>
              <Divider></Divider>
              <Row gutter={16}>
                <FormInput
                  col
                  xs={3}
                  name="flagholdback"
                  valuePropName="checked"
                >
                  <Switch
                    checkedChildren={<CheckOutlined />}
                    unCheckedChildren={<CloseOutlined />}
                    disabled={
                      (status === 'PROPOSAL' && fromproposal === false) ||
                      status === 'ORDER' ||
                      status === 'DELIVER'
                    }
                  />
                </FormInput>
                <FormItem shouldUpdate noStyle>
                  {({ getFieldValue }) =>
                    getFieldValue('flagholdback') ? (
                      <>
                        <FormInput col xs={24} md={4} name="holdbackpercentage">
                          <InputRate
                            disabled={
                              (status === 'PROPOSAL' &&
                                fromproposal === false) ||
                              status === 'ORDER' ||
                              status === 'DELIVER'
                            }
                          ></InputRate>
                        </FormInput>
                        <FormInput col xs={24} md={6} name="holdbackamount">
                          <InputCurrency
                            currcode={form.getFieldValue('currcode')}
                            disabled={
                              (status === 'PROPOSAL' &&
                                fromproposal === false) ||
                              status === 'ORDER' ||
                              status === 'DELIVER'
                            }
                          ></InputCurrency>
                        </FormInput>
                        <FormInput
                          col
                          xs={24}
                          md={3}
                          name="holdbackbeleasenbdays"
                        >
                          <InputNumber
                            disabled={
                              (status === 'PROPOSAL' &&
                                fromproposal === false) ||
                              status === 'ORDER' ||
                              status === 'DELIVER'
                            }
                          ></InputNumber>
                        </FormInput>
                        <FormInput
                          col
                          xs={24}
                          md={6}
                          name="holdbackbeleasedate"
                        >
                          <DatePicker
                            disabled={
                              (status === 'PROPOSAL' &&
                                fromproposal === false) ||
                              status === 'ORDER' ||
                              status === 'DELIVER'
                            }
                          ></DatePicker>
                        </FormInput>
                      </>
                    ) : null
                  }
                </FormItem>
              </Row>
            </Card>
          </TabPane>
          <TabPane
            tab={<GetLabel label="AssetPayementSchedule" />}
            key="2"
            disabled={isNil(compid) || iscreate}
          >
            <TabAssetComponentSchedule
              formC={formC}
              form={form}
              compId={compid}
            />
          </TabPane>
        </Tabs>
      </Form>
    </Modal>
  );
};

export default AssetComponentModal;
